import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import { Layout, Menu, Button, Upload, Table, Card, Typography, Space, Spin, Tabs, Badge, Progress, Statistic, Row, Col, Tag, Modal, Form, Input, Descriptions, Timeline, Alert, Divider, App as AntdApp, Popconfirm, ConfigProvider, message } from 'antd';
import { ProjectOutlined, FilePdfOutlined, UploadOutlined, EyeOutlined, SettingOutlined, HomeOutlined, DatabaseOutlined, ClockCircleOutlined, CheckCircleOutlined, ExclamationCircleOutlined, DeleteOutlined, BulbOutlined, MoonOutlined, BarChartOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import api, { initializeAPI } from './utils/api';

// Eagerly loaded: shell + core tabs that render on first paint
import Dashboard from './components/Dashboard';
import ProjectsGrid from './components/ProjectsGrid';
import DocumentsManager from './components/DocumentsManager';
import { UploadProgress, ProcessingProgress, DeleteProgress, CreateProgress } from './components/ProgressIndicator';
import PersistentProgressIndicator from './components/PersistentProgressIndicator';
import LandingPage from './components/LandingPage';
import AppLayout from './components/AppLayout';
import SignIn from './components/SignIn';
import SignUp from './components/SignUp';
import ProtectedRoute from './components/ProtectedRoute';
import AppLoadingWrapper from './components/AppLoadingWrapper';
import { UserDataProvider, useUserData } from './contexts/UserDataContext';

// Lazy-loaded: heavy (react-pdf, xlsx) or only used on demand.
// Splits the initial bundle and defers parse cost until these actually mount.
const PDFViewer = lazy(() => import('./components/PDFViewer'));
const UploadModal = lazy(() => import('./components/UploadModal'));
const CombinePDFsModal = lazy(() => import('./components/CombinePDFsModal'));
const SmartSearch = lazy(() => import('./components/SmartSearch'));
const GlobalSearch = lazy(() => import('./components/GlobalSearch'));
const InteractiveAnalytics = lazy(() => import('./components/InteractiveAnalytics'));
const EnhancedProjectModal = lazy(() => import('./components/EnhancedProjectModal'));
const Settings = lazy(() => import('./components/Settings'));
const About = lazy(() => import('./components/About'));
const Services = lazy(() => import('./components/Services'));
const Contact = lazy(() => import('./components/Contact'));
const Documentation = lazy(() => import('./components/Documentation'));

import { antdTheme } from './styles/theme';
import { darkTheme, lightTheme } from './styles/darkTheme';
import 'antd/dist/reset.css';
import './styles/modern.css';
import './styles/dark.css';
import './styles/design-system.css';
import './styles/app-redesign.css';

const { Header, Content, Footer, Sider } = Layout;
const { TabPane } = Tabs;
const { Title, Text, Paragraph } = Typography;

function AppContent() {
  const { getToken, isLoaded, isSignedIn } = useAuth();
  const { setProjectsLoaded } = useUserData();
  const navigate = useNavigate();
  const hasFetchedRef = React.useRef(false);

  // Initialize API and fetch data once when user is signed in.
  // Previously wrapped in setTimeout(100ms) "to ensure token is ready" - unnecessary,
  // the api interceptor awaits getToken() itself, so the data fetches were just
  // delayed by 100ms for no benefit.
  React.useEffect(() => {
    if (!isLoaded) return;
    initializeAPI(getToken);
    if (isSignedIn && !hasFetchedRef.current) {
      hasFetchedRef.current = true;
      fetchProjects();
      fetchDocuments();
      fetchTakeoffs();
    }
    if (!isSignedIn) {
      hasFetchedRef.current = false;
    }
  }, [isLoaded, isSignedIn]); // eslint-disable-line
  const [projects, setProjects] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [selectedProject, setSelectedProject] = React.useState(null);
  const [documents, setDocuments] = React.useState([]);
  const [takeoffs, setTakeoffs] = React.useState([]);
  const [activeTab, setActiveTab] = React.useState('projects');
  const [createModalVisible, setCreateModalVisible] = React.useState(false);
  const [projectDetailModal, setProjectDetailModal] = React.useState(false);
  const [uploadProgress, setUploadProgress] = React.useState(null);
  const [processingProgress, setProcessingProgress] = React.useState(null);
  const [deleteProgress, setDeleteProgress] = React.useState(null);
  const [createProgress, setCreateProgress] = React.useState(null);
  const [pdfViewerVisible, setPdfViewerVisible] = React.useState(false);
  const [selectedDocument, setSelectedDocument] = React.useState(null);
  const [uploadModalVisible, setUploadModalVisible] = React.useState(false);
  const [combineModalVisible, setCombineModalVisible] = React.useState(false);
  const [smartSearchVisible, setSmartSearchVisible] = React.useState(false);
  const [globalSearchVisible, setGlobalSearchVisible] = React.useState(false);
  const [darkMode, setDarkMode] = React.useState(() => {
    const saved = localStorage.getItem('darkMode');
    return saved ? JSON.parse(saved) : false;
  });
  const [autoSaveMeasurements, setAutoSaveMeasurements] = React.useState(() => {
    const saved = localStorage.getItem('autoSaveMeasurements');
    return saved ? JSON.parse(saved) : false;
  });
    const [form] = Form.useForm();

  // Dark mode effect
  React.useEffect(() => {
    if (darkMode) {
      document.body.classList.add('dark-mode');
    } else {
      document.body.classList.remove('dark-mode');
    }
    localStorage.setItem('darkMode', JSON.stringify(darkMode));
  }, [darkMode]);

  React.useEffect(() => {
    localStorage.setItem('autoSaveMeasurements', JSON.stringify(autoSaveMeasurements));
  }, [autoSaveMeasurements]);

  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
  };

  // Keyboard shortcuts - optimized with useCallback
  const selectedProjectRef = React.useRef(selectedProject);
  React.useEffect(() => {
    selectedProjectRef.current = selectedProject;
  }, [selectedProject]);

  React.useEffect(() => {
    const handleKeyDown = (event) => {
      // Ctrl/Cmd + K for global search
      if ((event.ctrlKey || event.metaKey) && event.key === 'k') {
        event.preventDefault();
        setGlobalSearchVisible(true);
      }
      
      // Ctrl/Cmd + N for new project
      if ((event.ctrlKey || event.metaKey) && event.key === 'n') {
        event.preventDefault();
        setCreateModalVisible(true);
      }
      
      // Ctrl/Cmd + U for upload
      if ((event.ctrlKey || event.metaKey) && event.key === 'u') {
        event.preventDefault();
        if (selectedProjectRef.current) {
          setUploadModalVisible(true);
        } else {
          message.info('Please select a project first');
        }
      }
      
      // Ctrl/Cmd + / for shortcuts
      if ((event.ctrlKey || event.metaKey) && event.key === '/') {
        event.preventDefault();
        // Shortcuts are now handled by AppLayout component
      }
      
      // Ctrl/Cmd + , for settings
      if ((event.ctrlKey || event.metaKey) && event.key === ',') {
        event.preventDefault();
        setActiveTab('settings');
      }
      
      // Escape to close modals
      if (event.key === 'Escape') {
        setCreateModalVisible(false);
        setProjectDetailModal(false);
        setPdfViewerVisible(false);
        setUploadModalVisible(false);
        setCombineModalVisible(false);
        setSmartSearchVisible(false);
      }
      
      // G + D for dashboard
      if (event.key === 'g' && !event.ctrlKey && !event.metaKey && !event.altKey) {
        const gTimeout = setTimeout(() => {
          // Only trigger if no other key was pressed quickly
          setActiveTab('dashboard');
        }, 200);
        
        const handleSecondKey = (e) => {
          clearTimeout(gTimeout);
          document.removeEventListener('keydown', handleSecondKey);
        };
        
        document.addEventListener('keydown', handleSecondKey);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []); // Empty dependency array - only attach once

  // Refresh data when selected project changes - debounced to avoid rapid API calls
  const fetchDocumentsTimeoutRef = React.useRef(null);
  React.useEffect(() => {
    if (fetchDocumentsTimeoutRef.current) {
      clearTimeout(fetchDocumentsTimeoutRef.current);
    }
    
    if (selectedProject) {
      // Debounce fetchDocuments by 300ms to avoid rapid API calls
      fetchDocumentsTimeoutRef.current = setTimeout(() => {
        fetchDocuments(selectedProject.id);
      }, 300);
    }
    
    return () => {
      if (fetchDocumentsTimeoutRef.current) {
        clearTimeout(fetchDocumentsTimeoutRef.current);
      }
    };
  }, [selectedProject]);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      const response = await api.get('/projects/');

      // Only update if we have data or if projects array is empty
      if (response.data.length > 0 || projects.length === 0) {
        setProjects(response.data);
      }

      // Mark projects as loaded
      setProjectsLoaded(true);
    } catch (error) {
      console.error('Failed to fetch projects:', error);
      // Don't clear projects on error - keep existing data
      if (error.code === 'ERR_NETWORK' || error.message?.includes('Network Error')) {
        message.error('Cannot connect to backend. Please start the server.');
      }
      setProjectsLoaded(true); // Mark as loaded even on error to not block UI
    } finally {
      setLoading(false);
    }
  };

  const fetchDocuments = async (projectId = null) => {
    try {
      const url = projectId 
        ? `/documents/?project_id=${projectId}`
        : '/documents/';
      const response = await api.get(url);
      setDocuments(response.data);
    } catch (error) {
      console.error('Failed to fetch documents:', error);
    }
  };

  const fetchTakeoffs = async () => {
    try {
      const response = await api.get('/takeoffs/items');
      setTakeoffs(response.data);
    } catch (error) {
      console.error('Failed to fetch takeoffs:', error);
    }
  };

  // Reset loading states (call this if stuck)
  const resetLoadingStates = () => {
    console.log('🔄 Resetting loading states...');
    setLoading(false);
    setUploadProgress(null);
    setProcessingProgress(null);
    
    // Clear any intervals
    if (window.processingInterval) {
      clearInterval(window.processingInterval);
      window.processingInterval = null;
    }
  };

  // Note: Removed auto-reset on page load as it was interfering with data loading
  // resetLoadingStates() can be called manually if needed

  const handleFileUpload = async (projectId, file, autoExtract = false, fastMode = false) => {
    console.log('Starting upload:', { projectId, fileName: file?.name, fileSize: file?.size, autoExtract, fastMode });
    
    const formData = new FormData();
    formData.append('file', file);

    const progressApi = window?.persistentProgress;
    const uploadId = progressApi?.addUpload?.(
      file?.name || 'PDF',
      autoExtract ? 'ai_extraction' : fastMode ? 'fast_mode' : 'upload_only'
    );
    
    try {
      setUploadProgress({ percent: 0, filename: file?.name || 'PDF' });
      
      // Choose endpoint based on mode
      let endpoint;
      if (fastMode) {
        endpoint = `/fast/upload-fast?project_id=${projectId}&fast_mode=true`;
      } else if (autoExtract) {
        endpoint = `/upload/upload-and-extract?project_id=${projectId}&auto_extract=true`;
      } else {
        endpoint = `/documents/upload?project_id=${projectId}`;
      }
      
      console.log('Sending POST to:', endpoint);
      progressApi?.updateUpload?.(uploadId, {
        status: 'processing',
        message: 'Uploading PDF...',
        progress: 5
      });
      
      const timeout = autoExtract ? 600000 : 120000;
      
      const response = await api.post(endpoint, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout,
        onUploadProgress: (progressEvent) => {
          if (!progressEvent.total) return;
          const percent = Math.round((progressEvent.loaded / progressEvent.total) * 100);
          setUploadProgress({ percent, filename: file?.name || 'PDF' });
          progressApi?.updateUpload?.(uploadId, {
            progress: percent,
            message: percent < 100 ? 'Uploading PDF...' : 'Upload complete, processing...'
          });
        },
      });
      
      if (!fastMode) {
        setProcessingProgress({ percent: 0, documentName: file?.name || 'Document' });
        let processingPercent = 0;
        window.processingInterval = setInterval(() => {
          processingPercent = Math.min(processingPercent + 10, 95);
          setProcessingProgress({ percent: processingPercent, documentName: file?.name || 'Document' });
          progressApi?.updateUpload?.(uploadId, {
            progress: processingPercent,
            message: autoExtract ? 'AI extraction running...' : 'Processing document...'
          });
          if (processingPercent >= 95) {
            clearInterval(window.processingInterval);
            window.processingInterval = null;
            setProcessingProgress(null);
          }
        }, 400);
      }
      
      console.log('Upload successful:', response.data);
      let successMsg;
      if (fastMode) {
        successMsg = `PDF "${file?.name || 'Document'}" uploaded! Fast mode started (${response.data.page_count || 'pending'} pages).`;
      } else if (autoExtract) {
        successMsg = `PDF "${file?.name || 'Document'}" uploaded! AI extraction running (${response.data.page_count || 'pending'} pages).`;
      } else {
        successMsg = `PDF "${file?.name || 'Document'}" uploaded successfully! (Processing in background)`;
      }
      message.success(successMsg);
      progressApi?.updateUpload?.(uploadId, {
        progress: 100,
        status: 'completed',
        message: 'Processing started in background'
      });
      
      const uploadArea = document.querySelector('.modern-upload');
      if (uploadArea) {
        uploadArea.classList.add('success-animation');
        setTimeout(() => {
          uploadArea.classList.remove('success-animation');
        }, 600);
      }
      
      await fetchDocuments(projectId);
      await fetchProjects();
      
      return response;
      
    } catch (error) {
      console.error('Upload failed:', error);
      console.error('Error response:', error.response?.data);
      
      if (uploadId) {
        progressApi?.updateUpload?.(uploadId, {
          status: 'failed',
          message: error.response?.data?.detail || error.message || 'Upload failed'
        });
      }
      
      if (window.processingInterval) {
        clearInterval(window.processingInterval);
        window.processingInterval = null;
      }
      
      const errorMsg = error.response?.data?.detail || error.message || 'Unknown error occurred';
      message.error({ content: `Upload failed: ${errorMsg}`, duration: 5 });
      
      return error;
      
    } finally {
      setUploadProgress(null);
    }
  };

  const handleCreateProject = async (values) => {
    let createInterval = null;
    try {
      setLoading(true);
      setCreateProgress({ percent: 0, itemName: 'Project' });
      
      // Simulate creation progress
      let createPercent = 0;
      createInterval = setInterval(() => {
        createPercent += 25;
        setCreateProgress({ percent: createPercent, itemName: 'Project' });
        if (createPercent >= 100) {
          clearInterval(createInterval);
          createInterval = null;
        }
      }, 150);
      
      await api.post('/projects/', values);
      
      // Clear interval before continuing
      if (createInterval) {
        clearInterval(createInterval);
        createInterval = null;
      }
      
      await fetchProjects();
      setCreateModalVisible(false);
      form.resetFields();
      message.success('Project created successfully!');
      
      // Add success animation
      const projectsGrid = document.querySelector('.projects-grid');
      if (projectsGrid) {
        projectsGrid.classList.add('create-success');
        setTimeout(() => {
          projectsGrid.classList.remove('create-success');
        }, 600);
      }
    } catch (error) {
      console.error('Failed to create project:', error);
      message.error('Failed to create project: ' + (error.response?.data?.detail || error.message));
    } finally {
      // Always clear interval and reset states
      if (createInterval) {
        clearInterval(createInterval);
      }
      setCreateProgress(null);
      setLoading(false);
    }
  };

  const handleEnhancedProjectCreate = async (projectData) => {
    let createInterval = null;
    try {
      setLoading(true);
      setCreateProgress({ percent: 0, itemName: 'Project' });
      
      // Simulate creation progress
      let createPercent = 0;
      createInterval = setInterval(() => {
        createPercent += 20;
        setCreateProgress({ percent: createPercent, itemName: 'Project' });
        if (createPercent >= 100) {
          clearInterval(createInterval);
          createInterval = null;
        }
      }, 200);
      
      // Create project first
      const projectResponse = await api.post('/projects/', {
        name: projectData.name,
        description: projectData.description,
        client_name: projectData.client_name,
        project_number: projectData.project_number,
        location: projectData.location,
        project_type: projectData.project_type,
        priority: projectData.priority,
        start_date: projectData.start_date,
        end_date: projectData.end_date,
        estimated_value: projectData.estimated_value
      });
      
      const projectId = projectResponse.data.id;
      
      // Upload project documents if any
      if (projectData.project_files && projectData.project_files.length > 0) {
        for (const file of projectData.project_files) {
          const formData = new FormData();
          formData.append('file', file);
          
          // project_id and auto_extract are query parameters
          await api.post(
            `/upload/upload-and-extract?project_id=${projectId}&auto_extract=false`, 
            formData, 
            {
              headers: { 'Content-Type': 'multipart/form-data' }
            }
          );
        }
      }
      
      // Process bid tab file if provided (backend endpoint not implemented yet)
      if (projectData.bid_tab_file) {
        try {
          const formData = new FormData();
          formData.append('bid_tab_file', projectData.bid_tab_file);
          await api.post(`/projects/${projectId}/import-bid-tab`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          message.info('Bid tab imported successfully');
        } catch (bidTabError) {
          console.warn('Bid tab import not yet implemented:', bidTabError.message);
          message.info('Bid tab file saved - import feature coming soon');
        }
      }
      
      await fetchProjects();
      setCreateModalVisible(false);
      message.success('Project created successfully with all documents!');
      
      // Add success animation
      const projectsGrid = document.querySelector('.projects-grid');
      if (projectsGrid) {
        projectsGrid.classList.add('create-success');
        setTimeout(() => {
          projectsGrid.classList.remove('create-success');
        }, 600);
      }
    } catch (error) {
      console.error('Failed to create enhanced project:', error);
      message.error({ content: 'Project creation failed: ' + (error.response?.data?.detail || error.message) });
    } finally {
      // Always clear interval and reset states
      if (createInterval) {
        clearInterval(createInterval);
      }
      setCreateProgress(null);
      setLoading(false);
    }
  };

  const handleViewProject = (project) => {
    setSelectedProject(project);
    setProjectDetailModal(true);
  };

  const handleUploadToProject = (project) => {
    setSelectedProject(project);
    setActiveTab('documents');
  };

  const handleNavigate = (tab) => {
    setActiveTab(tab);
  };

  const handleUploadClick = () => {
    if (!selectedProject) {
      message.info({ content: 'Please select a project first from the Projects tab' });
      setActiveTab('projects');
    } else {
      setUploadModalVisible(true);
    }
  };

  const handleClearProject = () => {
    setSelectedProject(null);
  };

  const handleCombineClick = () => {
    setUploadModalVisible(false);
    setCombineModalVisible(true);
  };

  const handleViewPDF = (document) => {
    // Use React Router navigate instead of window.location.href.
    // The old implementation triggered a full page reload, which re-downloaded
    // all JS/CSS assets, re-initialized Clerk, re-created the React tree, and
    // re-fetched projects/documents/takeoffs. This was a ~2-5s delay every time
    // the user opened a PDF. Client-side navigation is effectively instant.
    const params = new URLSearchParams({
      filename: document.original_filename,
      projectId: String(document.project_id),
    });
    navigate(`/app/viewer/${document.id}?${params.toString()}`);
  };

  const handleSmartSearchSelectProject = (project) => {
    setSelectedProject(project);
    setActiveTab('projects');
    message.success(`Selected project: ${project.name}`);
  };

  const handleSmartSearchSelectDocument = (document) => {
    handleViewPDF(document);
  };

  const handleDeleteDocument = async (documentId) => {
    try {
      setLoading(true);
      const document = documents.find(doc => doc.id === documentId);
      setDeleteProgress({ itemName: document?.original_filename || 'Document' });
      
      await api.delete(`/documents/${documentId}`);
      message.success('PDF deleted successfully!');
      
      // Add delete success animation
      try {
        const documentRow = window.document.querySelector(`[data-document-id="${documentId}"]`);
        if (documentRow) {
          documentRow.classList.add('delete-success');
          setTimeout(() => {
            // Refresh after animation completes
            fetchDocuments();
            fetchProjects();
          }, 500);
        } else {
          // Fallback if element not found
          await fetchDocuments();
          await fetchProjects();
        }
      } catch (domError) {
        console.warn('DOM animation failed, refreshing data:', domError);
        await fetchDocuments();
        await fetchProjects();
      }
      
      setTimeout(() => setDeleteProgress(null), 1000);
    } catch (error) {
      console.error('Delete failed:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Failed to delete PDF';
      message.error({ content: `Delete failed: ${errorMsg}` });
      setDeleteProgress(null);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteProject = async (project) => {
    try {
      setLoading(true);
      setDeleteProgress({ itemName: project.name });
      
      await api.delete(`/projects/${project.id}`);
      message.success(`Project "${project.name}" deleted successfully!`);
      
      // Add delete success animation
      try {
        const projectCard = window.document.querySelector(`[data-project-id="${project.id}"]`);
        if (projectCard) {
          projectCard.classList.add('delete-success');
          setTimeout(() => {
            // Refresh after animation completes
            fetchProjects();
          }, 500);
        } else {
          // Fallback if element not found
          await fetchProjects();
        }
      } catch (domError) {
        console.warn('DOM animation failed, refreshing data:', domError);
        await fetchProjects();
      }
      
      setTimeout(() => setDeleteProgress(null), 1000);
    } catch (error) {
      console.error('Delete failed:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Failed to delete project';
      message.error({ content: `Delete failed: ${errorMsg}` });
      setDeleteProgress(null);
    } finally {
      setLoading(false);
    }
  };

  const handleProcessDocument = async (documentId) => {
    try {
      setLoading(true);
      await api.post(`/documents/${documentId}/process`);
      
      // Refresh projects
      await fetchProjects();
      setLoading(false);
    } catch (error) {
      console.error('Processing failed:', error);
      setLoading(false);
    }
  };

  const sidebarMenuItems = [
    {
      key: 'dashboard',
      icon: <HomeOutlined />,
      label: 'Dashboard',
    },
    {
      key: 'analytics',
      icon: <BarChartOutlined />,
      label: 'Analytics',
    },
    {
      key: 'projects',
      icon: <ProjectOutlined />,
      label: 'Projects',
    },
    {
      key: 'documents',
      icon: <FilePdfOutlined />,
      label: 'Documents',
    },
    {
      key: 'takeoffs',
      icon: <DatabaseOutlined />,
      label: 'Takeoffs',
    },
      ];

  const projectColumns = [
    { 
      title: 'Project Name', 
      dataIndex: 'name', 
      key: 'name',
      render: (text, record) => (
        <Space>
          <ProjectOutlined style={{ color: '#1890ff' }} />
          <Text strong>{text}</Text>
        </Space>
      )
    },
    { title: 'Client', dataIndex: 'client_name', key: 'client_name' },
    { 
      title: 'Status', 
      dataIndex: 'is_active', 
      key: 'is_active',
      render: (isActive) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? 'Active' : 'Inactive'}
        </Tag>
      )
    },
    { 
      title: 'Created', 
      dataIndex: 'created_at', 
      key: 'created_at', 
      render: (text) => (
        <Space>
          <ClockCircleOutlined style={{ color: '#999' }} />
          <Text>{new Date(text).toLocaleDateString()}</Text>
        </Space>
      )
    },
    { 
      title: 'Documents', 
      key: 'document_count',
      render: (_, record) => (
        <Badge count={record.documents?.length || 0} showZero>
          <FilePdfOutlined style={{ fontSize: '16px' }} />
        </Badge>
      )
    },
    { 
      title: 'Actions', 
      key: 'actions',
      width: 200,
      render: (_, record) => (
        <Space>
          <Button 
            size="small" 
            icon={<EyeOutlined />}
            onClick={() => handleViewProject(record)}
          >
            View
          </Button>
          <Button 
            size="small" 
            type="primary"
            icon={<UploadOutlined />}
            onClick={() => {
              setSelectedProject(record);
              setActiveTab('documents');
            }}
          >
            Upload
          </Button>
        </Space>
      )
    }
  ];

  const documentColumns = [
    {
      title: 'Filename',
      dataIndex: 'original_filename',
      key: 'original_filename',
      render: (text) => (
        <Space>
          <FilePdfOutlined style={{ color: '#ff4d4f' }} />
          <Text>{text}</Text>
        </Space>
      )
    },
    {
      title: 'Status',
      dataIndex: 'processing_status',
      key: 'processing_status',
      render: (status) => {
        const statusConfig = {
          'pending': { color: 'orange', icon: <ClockCircleOutlined />, text: 'Pending' },
          'processing': { color: 'blue', icon: <Spin size="small" />, text: 'Processing' },
          'completed': { color: 'green', icon: <CheckCircleOutlined />, text: 'Completed' },
          'failed': { color: 'red', icon: <ExclamationCircleOutlined />, text: 'Failed' }
        };
        const config = statusConfig[status] || statusConfig.pending;
        return (
          <Tag color={config.color} icon={config.icon}>
            {config.text}
          </Tag>
        );
      }
    },
    {
      title: 'Pages',
      dataIndex: 'page_count',
      key: 'page_count'
    },
    {
      title: 'File Size',
      dataIndex: 'file_size',
      key: 'file_size',
      render: (size) => `${(size / 1024 / 1024).toFixed(2)} MB`
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (text) => new Date(text).toLocaleDateString()
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 200,
      render: (_, record) => (
        <Space>
          <Button
            size="small"
            icon={<EyeOutlined />}
            onClick={() => handleViewPDF(record)}
          >
            View PDF
          </Button>
          <Popconfirm
            title="Delete PDF"
            description="Are you sure you want to delete this PDF? This action cannot be undone."
            onConfirm={() => handleDeleteDocument(record.id)}
            okText="Yes, Delete"
            cancelText="Cancel"
            okButtonProps={{ danger: true }}
          >
            <Button
              size="small"
              danger
              icon={<DeleteOutlined />}
            >
              Delete
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  const takeoffColumns = [
    { title: 'Category', dataIndex: 'category', key: 'category' },
    { title: 'Description', dataIndex: 'description', key: 'description' },
    { title: 'Quantity', dataIndex: 'quantity', key: 'quantity' },
    { title: 'Unit', dataIndex: 'unit', key: 'unit' },
    { 
      title: 'Unit Price', 
      dataIndex: 'unit_price', 
      key: 'unit_price',
      render: (price) => `$${price?.toFixed(2) || '0.00'}`
    },
    { 
      title: 'Total', 
      key: 'total',
      render: (_, record) => {
        const total = (record.quantity || 0) * (record.unit_price || 0);
        return <Text strong>${total.toFixed(2)}</Text>;
      }
    }
  ];

  const suspenseFallback = (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
      <Spin size="large" />
    </div>
  );

  return (
    <ConfigProvider theme={darkMode ? darkTheme : lightTheme}>
      <AntdApp>
        <Suspense fallback={suspenseFallback}>
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/about" element={<About />} />
            <Route path="/services" element={<Services />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/docs" element={<Documentation />} />
            <Route path="/login/*" element={<SignIn />} />
            <Route path="/signup/*" element={<SignUp />} />
            <Route path="/app" element={
              <ProtectedRoute>
              <AppLoadingWrapper>
              <AppLayout
                activeTab={activeTab}
                onTabChange={setActiveTab}
              >
                {/* Progress Indicators */}
                {(uploadProgress || processingProgress || deleteProgress || createProgress) && (
                  <div className="card" style={{ marginBottom: 'var(--space-6)' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                      {uploadProgress && (
                        <UploadProgress 
                          percent={uploadProgress.percent} 
                          filename={uploadProgress.filename} 
                        />
                      )}
                      {processingProgress && (
                        <ProcessingProgress 
                          percent={processingProgress.percent} 
                          documentName={processingProgress.documentName} 
                        />
                      )}
                      {deleteProgress && (
                        <DeleteProgress itemName={deleteProgress.itemName} />
                      )}
                      {createProgress && (
                        <CreateProgress 
                          percent={createProgress.percent} 
                          itemName={createProgress.itemName} 
                        />
                      )}
                    </div>
                  </div>
                )}
                
                {/* Main Content */}
                {activeTab === 'dashboard' && (
                  <Dashboard 
                    projects={projects}
                    documents={documents}
                    onNavigate={handleNavigate}
                  />
                )}
                
                {activeTab === 'projects' && (
                  <ProjectsGrid
                    projects={projects}
                    onCreateProject={() => setCreateModalVisible(true)}
                    onViewProject={handleViewProject}
                    onUploadToProject={handleUploadToProject}
                    onDeleteProject={handleDeleteProject}
                  />
                )}
                
                {activeTab === 'analytics' && (
                  <InteractiveAnalytics
                    projects={projects}
                    documents={documents}
                    onNavigate={setActiveTab}
                  />
                )}
                
                {activeTab === 'settings' && (
                  <Settings
                    autoSaveMeasurements={autoSaveMeasurements}
                    onToggleAutoSaveMeasurements={setAutoSaveMeasurements}
                  />
                )}
                
                {activeTab === 'documents' && (
                  <DocumentsManager
                    documents={documents}
                    projects={projects}
                    selectedProject={selectedProject}
                    onViewPDF={handleViewPDF}
                    onDeleteDocument={handleDeleteDocument}
                    onUploadClick={handleUploadClick}
                    onClearProject={handleClearProject}
                  />
                )}
                
                {activeTab === 'takeoffs' && (
                  <div className="app-content-redesign">
                    <div className="dashboard-header-redesign">
                      <h1 className="dashboard-title">Takeoffs</h1>
                      <p className="dashboard-subtitle">
                        View and manage takeoff items across all projects
                      </p>
                    </div>
                    
                    <Table 
                      dataSource={takeoffs}
                      columns={takeoffColumns}
                      rowKey="id"
                      pagination={{ pageSize: 10 }}
                      summary={(pageData) => {
                        const totalAmount = pageData.reduce((sum, record) => {
                          return sum + ((record.quantity || 0) * (record.unit_price || 0));
                        }, 0);
                        return (
                          <Table.Summary.Row>
                            <Table.Summary.Cell index={0} colSpan={5}>
                              <Text strong>Total</Text>
                            </Table.Summary.Cell>
                            <Table.Summary.Cell index={5}>
                              <Text strong style={{ color: '#1890ff' }}>${totalAmount.toFixed(2)}</Text>
                            </Table.Summary.Cell>
                          </Table.Summary.Row>
                        );
                      }}
                    />
                  </div>
                )}
                
                {/* Modals inside AppLayout */}
                <EnhancedProjectModal
                  visible={createModalVisible}
                  onCancel={() => setCreateModalVisible(false)}
                  onCreate={handleEnhancedProjectCreate}
                  projects={projects}
                />

                <Modal
                  title="Project Details"
                  open={projectDetailModal}
                  onCancel={() => setProjectDetailModal(false)}
                  footer={[
                    <Button key="close" onClick={() => setProjectDetailModal(false)}>
                      Close
                    </Button>,
                    <Button 
                      key="upload" 
                      type="primary" 
                      icon={<UploadOutlined />}
                      onClick={() => {
                        setProjectDetailModal(false);
                        setActiveTab('documents');
                      }}
                    >
                      Upload PDF
                    </Button>
                  ]}
                  width={800}
                >
                  {selectedProject && (
                    <div>
                      <Descriptions column={2} bordered>
                        <Descriptions.Item label="Project Name" span={2}>
                          <Text strong>{selectedProject.name}</Text>
                        </Descriptions.Item>
                        <Descriptions.Item label="Client">
                          {selectedProject.client_name || 'N/A'}
                        </Descriptions.Item>
                        <Descriptions.Item label="Project Number">
                          {selectedProject.project_number || 'N/A'}
                        </Descriptions.Item>
                        <Descriptions.Item label="Location" span={2}>
                          {selectedProject.location || 'N/A'}
                        </Descriptions.Item>
                        <Descriptions.Item label="Description" span={2}>
                          {selectedProject.description || 'No description provided'}
                        </Descriptions.Item>
                        <Descriptions.Item label="Status">
                          <Tag color={selectedProject.is_active ? 'green' : 'red'}>
                            {selectedProject.is_active ? 'Active' : 'Inactive'}
                          </Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label="Created">
                      {new Date(selectedProject.created_at).toLocaleString()}
                    </Descriptions.Item>
                  </Descriptions>
                  
                  <Divider />

                  <Title level={5}>Project Documents</Title>
                  <Table
                    dataSource={documents.filter(d => d.project_id === selectedProject.id)}
                    columns={documentColumns}
                    rowKey="id"
                    pagination={{ pageSize: 5 }}
                    size="small"
                    style={{ marginBottom: '24px' }}
                  />

                  <Divider />
                  
                  <Title level={5}>Project Statistics</Title>
                  <Row gutter={16}>
                    <Col span={8}>
                      <Statistic
                        title="Documents"
                        value={selectedProject.documents?.length || 0}
                        prefix={<FilePdfOutlined />}
                      />
                    </Col>
                    <Col span={8}>
                      <Statistic
                        title="Takeoff Items"
                        value={0} // Would calculate from actual takeoffs
                        prefix={<DatabaseOutlined />}
                      />
                    </Col>
                    <Col span={8}>
                      <Statistic
                        title="Total Value"
                        value={0} // Would calculate from actual takeoffs
                        prefix="$"
                        precision={2}
                      />
                    </Col>
                  </Row>
                </div>
              )}
            </Modal>
              </AppLayout>
              </AppLoadingWrapper>
              </ProtectedRoute>
            } />
        
        <Route path="/project/:id" element={<div>Project Detail Page</div>} />
        <Route path="/app/viewer/:documentId" element={
          <ProtectedRoute>
            <PDFViewer />
          </ProtectedRoute>
        } />
      </Routes>
      </Suspense>

      {/* Lazy modals - only mounted when open so their chunks defer downloading */}
      <Suspense fallback={null}>
        {uploadModalVisible && (
          <UploadModal
            visible={uploadModalVisible}
            onClose={() => setUploadModalVisible(false)}
            selectedProject={selectedProject}
            onUpload={handleFileUpload}
            uploadProgress={uploadProgress}
            onCombineClick={handleCombineClick}
          />
        )}

        {combineModalVisible && (
          <CombinePDFsModal
            visible={combineModalVisible}
            onClose={() => setCombineModalVisible(false)}
            selectedProject={selectedProject}
          />
        )}

        {smartSearchVisible && (
          <SmartSearch
            visible={smartSearchVisible}
            onClose={() => setSmartSearchVisible(false)}
            projects={projects}
            documents={documents}
            onSelectProject={handleSmartSearchSelectProject}
            onSelectDocument={handleSmartSearchSelectDocument}
          />
        )}

        {globalSearchVisible && (
          <GlobalSearch
            visible={globalSearchVisible}
            onClose={() => setGlobalSearchVisible(false)}
          />
        )}
      </Suspense>

      {/* Persistent progress indicator (bottom-right, survives refresh) */}
      <PersistentProgressIndicator />

      </AntdApp>
    </ConfigProvider>
  );
}

function App() {
  return (
    <Router>
      <UserDataProvider>
        <AppContent />
      </UserDataProvider>
    </Router>
  );
}

export default App;
